import router from "@/router";
// Import directly from the source module (not the "@/services" barrel) to avoid
// a circular dependency: the barrel re-exports the service modules, which depend
// on "@/shared", which re-exports this file — routing back through the barrel.
import { getEnumAll } from "@/services/new-global-services";
import store from "@/store";
import { includes, pathOr } from "ramda";
import { inject, ref } from "vue";
// @ts-ignore
import { v4 as uuid } from "uuid";
// @ts-ignore
import DOMPurify from "isomorphic-dompurify";
import { fileTypeFromBuffer } from "file-type";

export async function detectFileType(file: any) {
  const buffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(buffer);
  const result = await fileTypeFromBuffer(uint8Array);
  const convertedExt: Record<string, string> = {
    opus: "ogg",
  };
  const ext =
    result?.ext && convertedExt[result.ext]
      ? convertedExt[result.ext]
      : result?.ext;

  return { ...result, ext };
}

export function random(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export const shuffleArray = (array: any) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};

export const storeDispatcher = async (
  key: string,
  value: any,
  module: any = null,
  isStringify = false
) => {
  const source = module ? `${module}/setAction` : "setAction";

  // @ts-ignore
  await store.dispatch(source, {
    key,
    value: isStringify ? JSON.stringify(value) : value,
  });
};
export const updateParvandehStatusDetail = async (value: any) => {
  await storeDispatcher("parvandehStatusDetail", value);
  await asyncLocalStorage.setItem(
    "parvandehStatusDetail",
    JSON.stringify(value)
  );
};
export const getParvandehStatusDetail = async () => {
  const storeData = generateStoreGetters(["parvandehStatusDetail"]);
  const localStorageData = await asyncLocalStorage.getItem(
    "parvandehStatusDetail"
  );
  return storeData || localStorageData
    ? storeData || JSON.parse(localStorageData)
    : null;
};
export const patchParvandehStatusDetail = async (
  tabName: string,
  data: any
) => {
  const parvandehStatusDetailObj = pathOr(
    null,
    ["parvandeh_status_detail"],
    data
  );
  const done = pathOr(false, ["complete_status"], parvandehStatusDetailObj);
  const subItems = pathOr([], ["message"], parvandehStatusDetailObj);

  let parvandehStatusDetail = await getParvandehStatusDetail();
  if (!parvandehStatusDetail) return;
  let totalIncompleteItemCount = pathOr(
    0,
    ["totalIncompleteItemCount"],
    parvandehStatusDetail
  );
  const items = pathOr([], ["items"], parvandehStatusDetail).map(
    (item: any) => {
      const currentDone = pathOr(false, ["done"], item);
      if (item.tabName === tabName) {
        if (currentDone !== done) {
          if (done) {
            totalIncompleteItemCount -= 1;
          } else {
            totalIncompleteItemCount += 1;
          }
          return { ...item, done, subItems };
        } else {
          return { ...item, subItems };
        }
      }
      return item;
    }
  );

  parvandehStatusDetail = {
    ...parvandehStatusDetail,
    items,
    totalIncompleteItemCount,
  };

  await updateParvandehStatusDetail(parvandehStatusDetail);
};

export const generateStoreGetters = (
  keys: Array<string>,
  namespace: string | null = null
) => {
  const stateData = namespace ? store.state[namespace] : store.state;
  const data = pathOr(null, keys, stateData);
  return data;
};

export const generateUrl = (
  url: string,
  replacement: string[],
  queryString: any = null,
  extraQueryString = ""
) => {
  for (const key in replacement) {
    if (Object.prototype.hasOwnProperty.call(replacement, key)) {
      // replace values
      url = url?.replace(`{${key}}`, replacement[key]);
    }
  }

  if (queryString) {
    url = generateQueryString(url, queryString);
  }

  url += extraQueryString;

  return url;
};

export const range = (start: any, end: any, step: any = 1) => {
  const len = Math.floor((end - start) / step) + 1;
  return Array(len)
    .fill(undefined, undefined, undefined)
    .map((_, idx: any) => start + idx * step);
};
export const nl2br = (str: string) => {
  return (str || "").replace(/(?:\r\n|\r|\n)/g, "<br>");
};
export const priceFormat = (value: number, currency = "") => {
  let output = `${Number(value || 0).toLocaleString()}`;
  output = currency ? `${output} ${currency}` : output;
  return output.trim();
};
export const addOrEditMetaTagToHead = (
  tagName: any,
  content = "",
  isEdit = false
) => {
  if (isEdit) {
    document.getElementsByTagName("meta")[tagName].content = "";
    return;
  }
  const tag = document.createElement("meta");
  tag.setAttribute("name", tagName);
  tag.content = content;
  document.getElementsByTagName("head")[0].appendChild(tag);
};
let isEnumerationLoading = false;
export const checkEnumeration = async () => {
  const { mutate: runGetEnumAll } = getEnumAll({ immediate: false });
  if (!localStorage.getItem("enumerations") && !isEnumerationLoading) {
    isEnumerationLoading = true;
    await runGetEnumAll({});
    isEnumerationLoading = false;
    window.location.reload();
  }
};
export const checkIsEditableEnum = (enumName: string) => {
  checkEnumeration();
  const enumerations =
    JSON.parse(localStorage.getItem("enumerations") || "[]") || [];
  const item = enumerations.find((row: any) => row.category_name === enumName);
  return pathOr(false, ["is_editable"], item);
};
export const renderEumOptions = (enumName: string, idTruck = false) => {
  checkEnumeration();
  const enumerations =
    JSON.parse(localStorage.getItem("enumerations") || "[]") || [];
  const item = enumerations.find((row: any) => row.category_name === enumName);
  const itemChild = [...pathOr([], ["child"], item)];
  return [...itemChild.map((item) => renderEnumItemFormat(item, idTruck))];
};

export const renderEumChildOptions = (enumItemId: any, idTruck = false) => {
  checkEnumeration();
  const enumerations = JSON.parse(localStorage.getItem("enumerations") || "");
  const childOptions: any = [];
  for (let i = 0; i < enumerations.length; i++) {
    const childArr = [...pathOr([], ["child"], enumerations[i])];
    childArr.forEach((item) => {
      if (pathOr(null, ["parent_id"], item) === enumItemId) {
        childOptions.push(renderEnumItemFormat(item, idTruck));
      }
    });
  }
  return childOptions;
};
export const renderEnumItemFormat = (item: any, idTruck = false) => {
  return idTruck
    ? {
        id: pathOr(null, ["id"], item),
        caption: pathOr(null, ["caption"], item),
        category_id: pathOr(null, ["category_id"], item),
        is_default: pathOr(false, ["is_default"], item),
      }
    : {
        label: pathOr(null, ["caption"], item),
        value: {
          label: pathOr(null, ["caption"], item),
          id: pathOr(null, ["id"], item),
          category_id: pathOr(null, ["category_id"], item),
          is_default: pathOr(false, ["is_default"], item),
        },
      };
};
export const renderEumOptionsSelected = (
  enumName: string,
  selectedValue: any
) => {
  // not used: will delete this method, but first check in all project
  return renderEumOptions(enumName).find((row) => row.value === selectedValue);
};
export const renderAllEnumeration = () => {
  checkEnumeration();
  const enumerations =
    JSON.parse(localStorage.getItem("enumerations") || "[]") || [];
  return [
    ...enumerations.map((item: any) => ({
      id: pathOr(null, ["category_name"], item),
      caption: pathOr(null, ["category_caption"], item),
    })),
  ];
};
export const fullScreen = (elemId: string) => {
  if (elemId) {
    const elem: any = document.getElementById(`${elemId}`);
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else {
      elem.exitFullscreen();
    }
  }
  return true;
};
export const checkPersianCharValidation = (str: any = null) => {
  if (!str) return;
  const regex = new RegExp(/[^\x00-\x7E]/);
  return str.match(regex);
};
export const checkMobileValidation = (str = "") => {
  const regex = new RegExp("^(\\+98|0)?9\\d{9}$");
  return str.match(regex);
};
export const convertUrlToBase64 = (url: any, callback: (prop: any) => void) => {
  const xhr = new XMLHttpRequest();
  xhr.onload = () => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.open("GET", url);
  xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
  xhr.responseType = "blob";
  xhr.send();
};
export const fileToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      } else {
        reject("Failed to read the file.");
      }
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};
export const generateExpireTime = (exTime: any, exSeconds: any) => {
  /* guidance: if exTime parameter is not a time => generate time 5 minute by default */
  const nowTime = new Date();
  const expireTime = exTime
    ? new Date(exTime).getTime()
    : nowTime.getTime() + exSeconds;
  return Math.round((expireTime - nowTime.getTime()) / 1000);
};
export const checkExpireTime = (exTime: any) => {
  if (!exTime) return false;
  const nowTime = new Date();
  const expireTime = exTime ? new Date(exTime).getTime() : nowTime.getTime();
  return expireTime - nowTime.getTime() > 0;
};

export const createDateFormat = (createdAt: string, showTime = true) => {
  if (!createdAt) return "";
  const dateArr = createdAt.split(" ").filter(Boolean);
  const parts = showTime ? [...dateArr].reverse() : dateArr.slice(0, 1);
  return parts
    .map((item) => `<span>${item.replaceAll("-", "/")}</span>`)
    .join(" - ");
};
export const checkExpiredTime = (date: any, limitDay = 1) => {
  const limit = limitDay * 86400;
  const now = new Date().getTime();
  const anotherDate = new Date(date).getTime();
  return Math.round((now - anotherDate) / 1000) > limit;
};
export const calcPercentage = (total = 0, inCompleteCount = 0) => {
  return Math.round((100 / total) * (total - inCompleteCount)) || 0;
};
export const isAdmin = (permission = [], allowed = null) => {
  // const roles = pathOr([], ["state", "profile", "userInfo", "roles"], store );
  const allowedArr = allowed || ["admin", "edit"];
  // const permissions = [...roles, ...permission]
  return allowedArr.some((item) => includes(item, permission));
};
export const checkNotNeededService = (methodName: any) => {
  /* guidance: check not needed service name in current route */

  if (!methodName) return false;
  // @ts-ignore
  const currentRouteName = router.history.current.name;
  const configObj = {
    getMenu: ["azmoon_execute_exam_pack"],
    getUserInfo: ["azmoon_execute_exam_pack"],
  };
  // @ts-ignore
  return includes(currentRouteName, configObj[methodName]);
};
export const downloadBlob = (data: any, downloadFileName: any = "") => {
  return new Promise((resolve, reject) => {
    try {
      const fileName = downloadFileName || uuid();
      // Check the Browser.
      // @ts-ignore
      const isIE = false || !!document.documentMode;
      if (isIE) {
        // @ts-ignore
        window.navigator.msSaveBlob(data, fileName);
      } else {
        const url = window.URL || window.webkitURL;
        const link = document.createElement("a");
        link.href = url.createObjectURL(data);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        resolve(true);
      }
    } catch (error) {
      reject(error);
    }
  });
};
export const getProjectName = () => {
  return import.meta.env.VUE_APP_PROJECT_NAME || "";
};
export const getWord = (str: string, num = 5, dots = false) => {
  if (!str) return str;
  const toArr = str.toString().split(" ");
  const output = toArr.filter((value, key) => key <= num - 1).join(" ");
  return dots ? `${output}...` : output;
};
export const getCharacter = (str: string, num = 5, dots = false) => {
  const myStr = String(str);
  if (!myStr) return myStr;
  const output = myStr.slice(0, num);
  return dots && myStr.length > num ? `${output}...` : output;
};

export const isActiveSmsPanel = () => {
  const isSmsActive = generateStoreGetters(["isSmsActive"]);
  const isAuthMobileLoginActive = generateStoreGetters([
    "isAuthMobileLoginActive",
  ]);
  return (
    isSmsActive &&
    isAuthMobileLoginActive &&
    import.meta.env.VUE_APP_ACTIVE_SMS_PANEL === "true"
  );
};
export const loginType = () => {
  return import.meta.env.VUE_APP_LOGIN_TYPE || "user-pass";
};
export const setUniqueArrayOfObjects = (data: any, key: string) => {
  return [...new Map(data.map((item: any) => [item[key], item])).values()];
};

export const setUniqueArray = (data: any, keyUnique: string) => {
  const uniqueIds = new Set();
  const uniqueArray = [];

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const element = data[key];
      if (!uniqueIds.has(element[keyUnique])) {
        uniqueIds.add(element[keyUnique]);
        uniqueArray.push(element);
      }
    }
  }
  return uniqueArray;
};

export const goToRoute = (
  name: string,
  params: object = {},
  query: object = {},
  newTab = true
) => {
  if (newTab) {
    // @ts-ignore
    const routeData = router.resolve({ name, params, query });

    window.open(routeData.href, "_blank");
  } else {
    // @ts-ignore
    router.push({ name, params, query });
  }
};

interface ExamPackQuestionnaireItem {
  url?: string;
  attemptCode?: string | null;
  porseshnamehCode?: string | null;
  azmoonExecutePersonCode?: string | null;
  porseshnamehPageCode?: string | null;
}

export const goToQuestionnaireAnswerAttemptsList = (
  item: ExamPackQuestionnaireItem,
  referrer: string = window.location.href
) => {
  const name = "questionnaire-answer-attempts-list";
  const code = pathOr(null, ["porseshnamehCode"], item);
  const azmoonPersonCode = pathOr(null, ["azmoonExecutePersonCode"], item);
  const pageCode = pathOr(null, ["porseshnamehPageCode"], item);
  const params = { code, azmoonPersonCode, pageCode };

  goToRoute(name, params, { referrer });
};

export const goToAzmoonExecuteQuestionnaire = (
  item: ExamPackQuestionnaireItem,
  referrer: string = window.location.href
) => {
  const name = "azmoon-execute-questionnaire";
  const code = pathOr(null, ["porseshnamehCode"], item);
  const azmoonExecutePersonCode = pathOr(
    null,
    ["azmoonExecutePersonCode"],
    item
  );
  const pageCode = pathOr(null, ["porseshnamehPageCode"], item);
  const attemptCode = pathOr(null, ["attemptCode"], item);
  const params = { code, azmoonExecutePersonCode, pageCode, attemptCode };

  goToRoute(name, params, { referrer });
};

export const handleCompleteQuestionnaire = (
  item: ExamPackQuestionnaireItem,
  closeTabAfterDone = false,
  referrer: string = window.location.href
) => {
  if ("attemptCode" in item) {
    if (item.attemptCode) {
      goToQuestionnaireAnswerAttemptsList(item, referrer);
    } else {
      goToAzmoonExecuteQuestionnaire(item, referrer);
    }
  } else {
    window.open(
      `${item?.url}?referrer=${referrer}${
        closeTabAfterDone ? "&closeTabAfterDone=true" : ""
      }`,
      "_blank"
    );
  }
};

export const justDigit = (str: any) => {
  // remove all characters except digits
  return str ? String(str).replaceAll(/\D/g, "").trim() : null;
};
export const checkNationalIdValidation = (nationalId: any) => {
  if (!nationalId) return true;
  if (nationalId.length != 10) return false;
  if (nationalId.substr(0, 3) == "000") return false;
  const invalidNumbers = [
    "0000000000",
    "1111111111",
    "2222222222",
    "3333333333",
    "4444444444",
    "5555555555",
    "6666666666",
    "7777777777",
    "8888888888",
    "9999999999",
  ];
  if (includes(nationalId, invalidNumbers)) return false;
  let check = 0;
  for (let i = 0; i < nationalId.length; i++) {
    const num = nationalId.substr(i, 1);
    check += num * (10 - i);
  }
  return !(check % 11);
};
export const checkNationalIdValidation2 = (val: any) => {
  if (!val) return true;

  const allDigitEqual = [
    "0000000000",
    "1111111111",
    "2222222222",
    "3333333333",
    "4444444444",
    "5555555555",
    "6666666666",
    "7777777777",
    "8888888888",
    "9999999999",
  ];
  const nationalCodePattern = /^([0-9]{10})+$/;
  if (allDigitEqual.indexOf(val) != -1 || !nationalCodePattern.test(val)) {
    return false;
  }
  const chArray = Array.from(String(val));
  const num0 = +chArray[0] * 10;
  const num2 = +chArray[1] * 9;
  const num3 = +chArray[2] * 8;
  const num4 = +chArray[3] * 7;
  const num5 = +chArray[4] * 6;
  const num6 = +chArray[5] * 5;
  const num7 = +chArray[6] * 4;
  const num8 = +chArray[7] * 3;
  const num9 = +chArray[8] * 2;
  const a = +chArray[9];
  const b = num0 + num2 + num3 + num4 + num5 + num6 + num7 + num8 + num9;
  const c = b % 11;
  return (c < 2 && a == c) || (c >= 2 && 11 - c == a);
};
export const logout = (shouldRefresh = false) => {
  localStorage.clear();
  indexedDB.deleteDatabase("imagesCached");

  if (shouldRefresh) {
    goToRoute("auth", {}, {}, false);
    location.reload();
  } else {
    goToRoute("auth", {}, {}, false);
  }
};
export const getActiveParvandeh = (key: any = null) => {
  try {
    const storeData = generateStoreGetters(["activeParvandeh"]);
    // const localStorageData = await asyncLocalStorage.getItem(
    //   "activeParvandeh",
    //   true
    // );

    const localStorageData = JSON.parse(
      localStorage.getItem("activeParvandeh") || ""
    );
    const activeParvandeh = storeData || localStorageData;
    return key ? pathOr(null, key, activeParvandeh) : activeParvandeh;
  } catch (error) {}
};

export const getProfileRequiredTabNames = (
  activeParvandeh: any = null,
  apiParvandeh: any = null
) => {
  const parvandeh = activeParvandeh || getActiveParvandeh();
  const localTabs = pathOr([], ["term", "profile_required_tab"], parvandeh);
  const apiTabs = pathOr([], ["term", "profile_required_tab"], apiParvandeh);

  return localTabs.length ? localTabs : apiTabs;
};

export const getIncompleteProfileRequiredTabs = (
  profileRequiredTabNames: string[],
  statusItems: { tabName: string; done: boolean }[] = []
) => {
  return statusItems.filter(
    ({ tabName, done }) => includes(tabName, profileRequiredTabNames) && !done
  );
};

export const shouldRedirectToProfileRequiredTabs = (
  profileRequiredTabNames: string[],
  statusItems: { tabName: string; done: boolean }[] = []
) => {
  return (
    getIncompleteProfileRequiredTabs(profileRequiredTabNames, statusItems)
      .length > 0
  );
};

export const getProfileRequiredTabsFromStatus = (
  profileRequiredTabNames: string[],
  statusItems: { tabName: string; done: boolean; [key: string]: any }[] = []
) => {
  return statusItems.filter(({ tabName }) =>
    includes(tabName, profileRequiredTabNames)
  );
};

export const resolveParvandehDefaultRoute = (
  parvandeh: any = null,
  fallback = "dashboard"
) => {
  const source = parvandeh || getActiveParvandeh();
  const defaultRoute = pathOr(fallback, ["term", "default_route"], source);

  return defaultRoute === "profile_info" ? "profile" : defaultRoute;
};

export const syncActiveParvandehTermFields = async (apiParvandeh: any) => {
  const activeParvandeh = getActiveParvandeh();
  if (!activeParvandeh || !apiParvandeh) return activeParvandeh;

  const term = { ...(activeParvandeh.term || {}) };
  let updated = false;

  const apiProfileRequiredTab = pathOr(
    [],
    ["term", "profile_required_tab"],
    apiParvandeh
  );
  const localProfileRequiredTab = pathOr(
    [],
    ["term", "profile_required_tab"],
    activeParvandeh
  );
  if (apiProfileRequiredTab.length && !localProfileRequiredTab.length) {
    term.profile_required_tab = apiProfileRequiredTab;
    updated = true;
  }

  const apiDefaultRoute = pathOr(null, ["term", "default_route"], apiParvandeh);
  const localDefaultRoute = pathOr(
    null,
    ["term", "default_route"],
    activeParvandeh
  );
  if (apiDefaultRoute && !localDefaultRoute) {
    term.default_route = apiDefaultRoute;
    updated = true;
  }

  if (!updated) return activeParvandeh;

  const updatedActiveParvandeh = { ...activeParvandeh, term };
  localStorage.setItem(
    "activeParvandeh",
    JSON.stringify(updatedActiveParvandeh)
  );
  await storeDispatcher("activeParvandeh", updatedActiveParvandeh);

  return updatedActiveParvandeh;
};

export const persistActiveParvandehDefaultRoute = async (
  defaultRoute: string,
  profileRequiredTab: string[] = []
) => {
  const activeParvandeh = getActiveParvandeh();
  if (!activeParvandeh) return;

  const normalizedDefaultRoute =
    defaultRoute === "profile_info" ? "profile" : defaultRoute;
  const updatedActiveParvandeh = {
    ...activeParvandeh,
    term: {
      ...(activeParvandeh.term || {}),
      default_route: normalizedDefaultRoute,
      ...(profileRequiredTab.length
        ? { profile_required_tab: profileRequiredTab }
        : {}),
    },
  };

  localStorage.setItem(
    "activeParvandeh",
    JSON.stringify(updatedActiveParvandeh)
  );
  await storeDispatcher("activeParvandeh", updatedActiveParvandeh);

  return updatedActiveParvandeh;
};
export const getParvandehCount = () => {
  const parvandehCount = localStorage.getItem("parvandehCount") || 1;
  return parvandehCount;
};
export const checkPermission = (permission = [], allowed = null) => {
  const allowedArr = allowed || ["admin", "edit", "view"];
  return allowedArr.some((item) => includes(item, permission));
};
export const generateQueryString = (url = "", queryString: any = {}) => {
  let output = url;
  const queryStringKeys = Object.keys(queryString);
  if (queryStringKeys.length) {
    output = `${output}?`;
    for (const key of queryStringKeys) {
      const value = queryString[key];
      if (value) {
        output = `${output}${key}=${value}&`;
      }
    }
    output = output.slice(0, -1); // remove last &
  }
  return output;
};
export const generatePayloadNew = (
  ctx: any,
  payload: any,
  extraPayload: any = {}
) => {
  const lastPayload: any = {};
  Object.entries(payload).forEach(([key, value]: any) => {
    lastPayload[key] = ctx[value];
  });
  const getPayload = {
    ...lastPayload,
    ...extraPayload,
    // replacement: bodyObj.replacement,
  };

  return getPayload;
};

export const generatePayload = (
  ctx: any,
  payload: any,
  extraPayload: any = {},
  extraHeaders: any = {}
) => {
  const bodyObj: any = ctx.options.body;

  if (bodyObj instanceof FormData) {
    const newFormData = new FormData();
    Object.entries(payload).forEach(([firstKey, secondKey]: any) => {
      const value: any = bodyObj.get(secondKey);

      value && newFormData.append(firstKey, value);
    });

    Object.entries(extraPayload).forEach(([key, value]: any) => {
      newFormData.append(key, value);
    });
    ctx.options.body = newFormData;
  } else {
    const lastPayload: any = {};
    Object.entries(payload).forEach(([key, value]: any) => {
      lastPayload[key] = bodyObj[value];
    });

    const getPayload = {
      ...lastPayload,
      ...extraPayload,
      replacement: bodyObj.replacement,
    };

    ctx.options.body = JSON.stringify(getPayload);
  }

  ctx.options.headers = {
    ...ctx.options.headers,
    ...extraHeaders,
  };

  return ctx;
};
export const generateQueryStringWithKeysRepeat = (
  items: any = [],
  repeatKey = "repeatKey",
  extraQueryStrings: any = null
) => {
  // generate a query string when my query key is repeated
  let queryString = "?";
  for (const item of items) {
    queryString = `${queryString}${repeatKey}[]=${item}&`;
  }
  if (extraQueryStrings) {
    for (const [key, value] of Object.entries(extraQueryStrings)) {
      queryString += `${key}=${value}&`;
    }
  }
  queryString = queryString.slice(0, -1); // remove last & of queryString

  return queryString;
};
export const checkScreenDevice = (deviceArr: any = []) => {
  try {
    const screenSize = ref(window.innerWidth);
    window.addEventListener("resize", () => {
      screenSize.value = window.innerWidth;
    });

    let screenDevice = "desktop";
    if (Number(screenSize.value) <= 1200) {
      screenDevice = "tablet";
    }
    if (Number(screenSize.value) <= 768) {
      screenDevice = "mobile";
    }
    return includes(screenDevice, deviceArr);
  } catch (e) {}
};
export const generateCurrentRoute = (key: any = ["name"]) => {
  return pathOr(null, key, router.currentRoute.value);
};
export const generateProfileServiceKey = () => {
  const routeName = pathOr("", ["name"], router.currentRoute.value);
  const routeNameSplitted = routeName.split("-");
  return routeNameSplitted[0];
};
export const generateRouteData = (name: string, path: any) => {
  const routeData = router.resolve({ name });
  return path ? pathOr(null, path, routeData) : routeData;
};
export const getRandomPasswordLength = () => {
  return import.meta.env.VUE_APP_RANDOM_PASSWORD_LENGTH || 8;
};
export const generateRandomPassword: (
  length?: number,
  withSymbol?: boolean
) => string = (length = 8, withSymbol = false): string => {
  const charsetAndSymbol =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@$!%*#?&)^(_=+-";
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const chars = withSymbol ? charsetAndSymbol : charset;
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    password += chars[randomIndex];
  }
  const passwordRule = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[@$!%*#?&)^(_=+-]).+$/;

  return passwordRule.test(password)
    ? password
    : generateRandomPassword(length, withSymbol);
};

export const sleep = (milliSeconds = 2000) => {
  return new Promise((resolve) => setTimeout(resolve, milliSeconds));
};
export const removeInArray = (removeItem: any, items: any) => {
  const index = items.findIndex((item: any) => item === removeItem);
  if (index !== -1) {
    items.splice(index, 1);
  }
};
export const generateRouteLink = (
  name: string,
  params: object = {},
  query: object = {}
) => {
  // @ts-ignore
  const routeData = router.resolve({ name, params, query });
  return routeData.href;
};
export const generateFileType = (fileSuffix: string) => {
  if (includes(fileSuffix, ["jpg", "jpeg", "png", "bmp", "webp"])) {
    return "image";
  } else if (
    includes(fileSuffix, [
      "mp3",
      "wav",
      "ogg",
      "webm",
      "flac",
      "aac",
      "wma",
      "aiff",
      "alac",
      "opus",
      "amr",
      "3gp",
      "m4a",
    ])
  ) {
    return "audio";
  } else if (includes(fileSuffix, ["mp4", "wmv", "wave"])) {
    return "video";
  } else if (includes(fileSuffix, ["pdf"])) {
    return "pdf";
  } else if (
    includes(fileSuffix, ["xls", "xlsx", "doc", "docx", "ppt", "pptx"])
  ) {
    return "document";
  } else {
    return "file";
  }
};

export const copy = (vm: any, text: string, notify = false) => {
  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  notify && vm.$notify.success(`متن "${text}" کپی شد`);
};

export const checkPersianChar = (vm: any, str = "", ev: any) => {
  const regex = new RegExp(/[^\x00-\x7E]/);
  if (ev.keyCode == 8 || ev.keyCode == 46) return false;
  if (str && str.length == 1 && str.match(regex)) {
    vm.$notify.error(vm.$lang.persion_caracter);
  }
};
export const generateApiUrl = (
  vm: any,
  keys: Array<string> = [],
  replacement: any = [],
  queryString: any = {},
  baseUrl: any = ["api_url"]
) => {
  /* guidance: generate api url by keys and replacement values */

  // @ts-ignore
  let url = pathOr(null, baseUrl, vm.$config) + pathOr(null, keys, vm.$config);
  for (const key in replacement) {
    if (Object.prototype.hasOwnProperty.call(replacement, key)) {
      // replace values
      url = url.replace(`{${key}}`, replacement[key]);
    }
  }
  // add query strings to url

  url = generateQueryString(url, queryString);
  return url;
};

export const parseStringify = (data: any) => {
  return JSON.parse(data);
};
export const generateApi = (
  keys: any = [],
  replacement: any = [],
  queryString: any = {}
) => {
  /* guidance: generate api url by keys and replacement values */
  const config = inject("config");

  // @ts-ignore
  let url = pathOr(undefined, keys, config)
    ? pathOr(undefined, keys, config)
    : "";
  for (const key in replacement) {
    if (Object.prototype.hasOwnProperty.call(replacement, key)) {
      // replace values
      url = url?.replace(`{${key}}`, replacement[key]);
    }
  }
  // add query strings to url

  url = generateQueryString(url, queryString);
  return url;
};
export const checkCustomKey = (vm: any, key: string, showDefault = true) => {
  return pathOr(showDefault, [key], vm.$customConfig);
};

export const checkActivePersonRelation = (vm: any, item: any) => {
  // allow person relation when turn on key in custom config or this item has relation_count
  const relationCount = pathOr(0, ["relation_count"], item);
  return vm.$customConfig.isRegisterPersonRelation || relationCount;
};
export const generateIndicatorImage = (vm: any, type: any, cls = "") => {
  if (!type) return "";
  const imageSrc =
    type === 1
      ? vm.$config.images.shared.redesign.successCircle
      : vm.$config.images.shared.redesign.errorCircle;
  return `<img src="${imageSrc}" class="${cls}"/>`;
};
export const toastAlert = (vm: any, message: string, type = "success") => {
  vm.$notify[type](message);
};
export const generateError = (vm: any, error: any) => {
  vm.$generateError(error);
};
export const generateLang = (vm: any, key: any) => {
  return vm.$lang(key);
};
export const generateConfig = (vm: any, key: any) => {
  return pathOr("", key, vm.$config);
};
export const renderAvatar = (
  data: any,
  type = "avatar",
  addressKey = "address"
) => {
  const imgKind = pathOr([], ["img_kind"], data);
  const imgItem = imgKind.find(
    (img: any) => pathOr(null, ["type"], img) === type
  );
  const address = pathOr("", [addressKey], imgItem);
  return address;
};

export const generateLanguage = (vm: any, key: string) => vm.$lang(key);

export const createArray = (length: any = 5) => {
  return Array.from({ length }, (_, index) => index);
};

export const asyncLocalStorage = {
  setItem(key: string, value: string) {
    return Promise.resolve().then(() => localStorage.setItem(key, value));
  },
  getItem(key: string, isJasonParse = false) {
    const localStorageValue: any = localStorage.getItem(key);
    return Promise.resolve().then(() =>
      isJasonParse ? JSON.parse(localStorageValue) : localStorageValue
    );
  },
  removeItem(key: string) {
    const localStorageValue: any = localStorage.removeItem(key);
    return Promise.resolve().then(() => localStorageValue);
  },
  clear() {
    const localStorageValue: any = localStorage.clear();
    return Promise.resolve().then(() => localStorageValue);
  },
};

export const asyncSome = async (arr: any, predicate: any) => {
  for (const e of arr) {
    const check = await predicate(e);
    if (check) {
      return true;
    }
  }
  return false;
};

export const generateFullName = (data: object | null) => {
  if (!data) return "";
  const name = pathOr("", ["name"], data);
  const family = pathOr("", ["family"], data);
  return [name, family].filter(Boolean).join(" ");
};

export const setAuthData = async (data: any) => {
  try {
    const tokenType = pathOr("bearer", ["token_type"], data);
    const accessToken = pathOr(null, ["access_token"], data);
    const refreshToken = pathOr(null, ["refresh_token"], data);
    // TODO: remove dev codes

    const accessTokenExpiredAt = pathOr(0, ["access_token_expired_at"], data);
    const refreshTokenExpiredAt = pathOr(0, ["refresh_token_expired_at"], data);

    const createdAt = new Date().getTime();
    const authData = {
      tokenType,
      accessToken,
      refreshToken,
      accessTokenExpiredAt,
      refreshTokenExpiredAt,
      createdAt,
    };
    await asyncLocalStorage.setItem("authData", JSON.stringify(authData));
  } catch (e) {
    //
  }
};

export const checkAuthenticated = async () => {
  try {
    const activeParvandeh = await asyncLocalStorage.getItem("activeParvandeh");
    if (!activeParvandeh) return false;
    const activeParvandehData = JSON.parse(activeParvandeh || "") || {};
    return !!activeParvandehData;
  } catch (e) {
    return false;
  }
};
export const generateUserData = (item: object) => {
  const downloadCode1 = pathOr(null, ["downloadCode"], item);
  const downloadCode2 = pathOr(null, ["download_code"], item);
  const downloadCode3 = pathOr(null, ["person", "download_code"], item);
  const personCode1 = pathOr(null, ["person", "code"], item);
  const personCode2 = pathOr(null, ["personCode"], item);
  const personCode3 = pathOr(null, ["person_code"], item);
  const personCode4 = pathOr(null, ["code"], item);
  const personCode5 = pathOr(null, ["parvandeh", "person", "code"], item);
  const parvandehCode1 = pathOr(null, ["parvandeh", "code"], item);
  const parvandehCode2 = pathOr(null, ["parvandeh", "parvandeh_code"], item);
  const parvandehCode3 = pathOr(null, ["parvandeh_code"], item);
  const parvandehCode4 = pathOr(null, ["person", "parvandeh_code"], item);
  const parvandehCode5 = pathOr(null, ["parvandehCode"], item);
  const parvandehCode6 = pathOr(null, ["parvandeh", "code"], item);
  const avatar1 = pathOr("", ["person", "image_address"], item);
  const avatar2 = pathOr("", ["image_address"], item);
  const avatar3 = pathOr("", ["image"], item);
  const fullName1 = pathOr("", ["person", "full_name"], item);
  const fullName2 = pathOr("", ["full_name"], item);
  const name = pathOr("", ["person", "name"], item);
  const family = pathOr("", ["person", "family"], item);
  const fullName3 = name && family ? `${name} ${family}` : "";
  const fullName4 = pathOr("", ["name"], item);
  const fullName5 = pathOr("", ["person", "name"], item);
  const fullName6 = pathOr("", ["fullName"], item);
  const name2 = pathOr("", ["parvandeh", "person", "name"], item);
  const family2 = pathOr("", ["parvandeh", "person", "family"], item);
  const fullName7 = name2 && family2 ? `${name2} ${family2}` : "";
  const mobile1 = pathOr("", ["person", "mobile"], item);
  const mobile2 = pathOr("", ["parvandeh", "person", "mobile"], item);
  const mobile3 = pathOr("", ["mobile"], item);
  return {
    downloadCode: downloadCode1 || downloadCode2 || downloadCode3,
    personCode:
      personCode1 || personCode2 || personCode3 || personCode4 || personCode5,
    parvandehCode:
      parvandehCode1 ||
      parvandehCode2 ||
      parvandehCode3 ||
      parvandehCode4 ||
      parvandehCode5 ||
      parvandehCode6,
    avatar: avatar1 || avatar2 || avatar3,
    fullName:
      fullName1 ||
      fullName2 ||
      fullName3 ||
      fullName4 ||
      fullName5 ||
      fullName6 ||
      fullName7,
    mobile: mobile1 || mobile2 || mobile3,
  };
};
export const arrayToString = (data: any, keys: any, separator: any = ", ") => {
  const dataArr = Array.isArray(data) ? data : [];
  return dataArr.map((item) => pathOr(null, keys, item)).join(separator);
};
export const arrayToStringByBoolean = (data: any, separator: any = " - ") => {
  const dataArr = Array.isArray(data) ? data : [];
  return dataArr.filter(Boolean).join(separator);
};
export const imageUrlToFileObject = async (imageUrl: any) => {
  // Fetch the image data as a blob
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  // Get the file name from the image URL
  const fileName = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);

  // Create a File object from the blob
  const file = new File([blob], fileName, { type: blob.type });

  return file;
};
export const removeHtmlTags = (htmlString: string) => {
  return htmlString.replace(/<[^>]*>/g, "");
};
export const executeAsync = (
  callback: () => void = () => {}
): Promise<void> => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      callback();
      resolve();
    }, 0);
  });
};

export const createPureDom = (encodedHtml = "") => {
  if (!encodedHtml) return "";
  const temp = document.createElement("textarea");
  temp.innerHTML = encodedHtml;
  return DOMPurify.sanitize(temp.value, {
    ALLOWED_ATTR: ["style", "class", "src"],
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "applet",
      "link",
      "meta",
      "style",
      "form",
      "input",
      "button",
      "textarea",
      "select",
      "svg",
      "math",
    ],
    ALLOWED_URI_REGEXP: /^data:image\/(png|jpeg|jpg|gif|webp);base64,/,
  });
};

export const extractDeviceAndBrowser = (userAgent: any) => {
  let browserName = "Unknown Browser";
  let deviceName = "Unknown Device";

  // Detect browser
  if (/chrome|crios/i.test(userAgent) && !/opr|opera|edg/i.test(userAgent)) {
    browserName = "Chrome";
  } else if (/firefox|fxios/i.test(userAgent)) {
    browserName = "Firefox";
  } else if (/safari/i.test(userAgent) && !/chrome|crios/i.test(userAgent)) {
    browserName = "Safari";
  } else if (/edg/i.test(userAgent)) {
    browserName = "Edge";
  } else if (/opr|opera/i.test(userAgent)) {
    browserName = "Opera";
  } else if (/msie|trident/i.test(userAgent)) {
    browserName = "Internet Explorer";
  }

  // Detect device
  if (/android/i.test(userAgent)) {
    deviceName = "Android";
  } else if (/iPad|iPhone|iPod/.test(userAgent)) {
    deviceName = "iOS";
  } else if (/Windows NT|Windows/i.test(userAgent)) {
    deviceName = "Windows";
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    deviceName = "Mac";
  } else if (/Linux/i.test(userAgent)) {
    deviceName = "Linux";
  }

  return `${deviceName} - ${browserName}`;
};

function isPersianNumber(number: any) {
  // این عبارت با قاعده برای بررسی اعداد فارسی استفاده می‌شود
  const persianNumberRegex = /^[\u06F0-\u06F9]+$/;
  return persianNumberRegex.test(number);
}

export const convertToEnglishNumber = (persianNumber: string) => {
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const englishDigits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  return isPersianNumber(persianNumber)
    ? persianNumber.replace(/[۰-۹]/g, (char) => {
        return englishDigits[persianDigits.indexOf(char)];
      })
    : persianNumber;
};

export function formatFileSize(bytes: number, lang: any) {
  if (bytes === 0) return "0 بایت";
  const k = 1024;
  const sizes = [
    lang("byte"),
    lang("kilobyte"),
    lang("megaByte"),
    lang("gigaByte"),
    lang("terabyte"),
  ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(1)} ${sizes[i]}`;
}

export function isBlob(obj: any) {
  return obj instanceof Blob;
}

export function formatBlobSize(blob: any, decimals = 2) {
  if (!blob || typeof blob.size !== "number") return "0 بایت";
  const bytes = blob.size;
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت", "ترابایت"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function formatDuration(seconds: any) {
  seconds = Math.floor(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let result = "";
  if (hours > 0) {
    result += hours + " ساعت";
    if (minutes > 0) {
      result += " و " + minutes + " دقیقه";
    }
  } else if (minutes > 0) {
    result += minutes + " دقیقه";
    if (secs > 0) {
      result += " و " + secs + " ثانیه";
    }
  } else {
    result += secs + " ثانیه";
  }

  return result;
}

export function swapElements(array: any, index1: any, index2: any) {
  if (
    index1 >= 0 &&
    index2 >= 0 &&
    index1 < array.length &&
    index2 < array.length
  ) {
    const newArray = [...array];
    [newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]];
    return newArray;
  }
  return array;
}

export function generateFileSize(file: any): string {
  const bytes = file?.size || 0;

  if (bytes === 0) return "0 B";

  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  const index = Math.min(i, sizes.length - 1);
  const sizeValue = bytes / Math.pow(1024, index);

  return `${parseFloat(sizeValue.toFixed(2))} ${sizes[index]}`;
}

export function timeAgo(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [unit, seconds] of Object.entries(intervals)) {
    const count = Math.floor(diffInSeconds / seconds);
    if (count >= 1) {
      switch (unit) {
        case "year":
          return `${count} سال پیش`;
        case "month":
          return `${count} ماه پیش`;
        case "week":
          return `${count} هفته پیش`;
        case "day":
          return `${count} روز پیش`;
        case "hour":
          return `${count} ساعت پیش`;
        case "minute":
          return `${count} دقیقه پیش`;
        default:
          return `لحظاتی پیش`;
      }
    }
  }
  return "لحظاتی پیش";
}
export const isBaleActive = () => {
  return import.meta.env.VUE_APP_BALE_ACTIVE === "true";
};
// currentPageStartQuestionNumber = 0,
// index = 0
export const generateQuestionnaireItem = (
  item: object,
  itemNumber: number | null
) => {
  const answerAttachmentData = pathOr(null, ["answer_attachment"], item);
  const size = pathOr(0, ["size"], answerAttachmentData);
  const answerAttachments = answerAttachmentData
    ? [
        {
          code: pathOr(null, ["code"], answerAttachmentData),
          attachment_type_id: pathOr(
            0,
            ["attachment_type_id"],
            answerAttachmentData
          ),
          caption: pathOr("", ["caption"], answerAttachmentData),
          size: generateFileSize({ size }),
          attachmentTypeCaption: pathOr(
            "",
            ["attachment_type_caption"],
            answerAttachmentData
          ),
          attachmentCode: pathOr(
            null,
            ["attachment_code"],
            answerAttachmentData
          ),
          downloadCode: pathOr(
            null,
            ["attachment_download_code"],
            answerAttachmentData
          ),
        },
      ]
    : [];
  const answer = pathOr(null, ["answer"], item);
  let answerValue = null;
  let answerDescription: any = null;
  let optionIds = [];
  let optionItems = [];

  if (Array.isArray(answer)) {
    optionIds = [
      ...pathOr([], ["answer"], item).map((item: any) => item?.option_id),
    ];
    optionItems = [
      ...pathOr([], ["answer"], item).map((item: any) => ({
        id: item?.option_id,
        caption: item?.answer_description,
      })),
    ];
  } else {
    answerValue = pathOr(null, ["answer_value"], answer);
    answerDescription = pathOr(null, ["answer_description"], answer);
    const optionId = pathOr(null, ["option_id"], answer);
    optionIds = optionId ? [optionId] : [];
    optionItems = optionId
      ? [
          {
            id: optionId,
            caption: pathOr(null, ["answer_description"], answer),
          },
        ]
      : [];
  }

  const code = pathOr(null, ["code"], item);
  const extraData = pathOr("", ["extra_data"], item);
  let extraDataObj = JSON.parse(extraData);
  const configs = pathOr([], ["configs"], extraDataObj);
  const updatedConfigs = configs.map((config: any) => {
    if (config?.isResponse) {
      let value = answerValue;
      let multipleChoiceValue: any[] = [];
      let multipleChoiceSelectedItems: any[] = [];
      let singleChoiceValue = null;
      let singleChoiceSelectedItem = null;

      // long text
      if (includes(config.type, ["longText"])) {
        value = answerDescription;
      }

      // dropdown
      if (config.type === "dropdown") {
        value = optionIds[0];
      }

      // multiple choice | single choice | multiple choice with annotation
      if (
        includes(config.type, [
          "multipleChoice",
          "imageMultipleChoice",
          "multipleChoiceWithAnnotation",
        ])
      ) {
        if (config.isMultipleChoiceQuestion) {
          multipleChoiceValue = optionIds;
          multipleChoiceSelectedItems = optionItems;
        } else {
          singleChoiceValue = optionIds[0];
          singleChoiceSelectedItem = optionItems[0];
        }
      }

      if (includes(config.type, ["responsePattern"])) {
        const isNumericField = config?.input?.id === "number";
        if (isNumericField) {
          value = answerValue;
        } else {
          value = answerDescription;
        }
      }

      const lastConfig = { ...config, value };
      if (
        includes(config.type, [
          "multipleChoice",
          "imageMultipleChoice",
          "multipleChoiceWithAnnotation",
        ])
      ) {
        lastConfig.multipleChoiceValue = multipleChoiceValue;
        lastConfig.multipleChoiceSelectedItems = multipleChoiceSelectedItems;
        lastConfig.singleChoiceValue = singleChoiceValue;
        lastConfig.singleChoiceSelectedItem = singleChoiceSelectedItem;
      }

      if (includes(config.type, ["multipleChoiceWithAnnotation"])) {
        const annotationItem = (answer || []).find(
          (item) => !pathOr(null, ["option_id"], item)
        );
        lastConfig.annotationValue = pathOr(
          null,
          ["answer_description"],
          annotationItem
        );
      }

      // rating | spectral
      if (includes(config.type, ["rating", "spectral"])) {
        lastConfig.rate = pathOr(null, ["0", "answer_value"], answer);
      }

      // user uploader
      if (includes(config.type, ["userUploader"])) {
        lastConfig.currentFiles = answerAttachments;
      }
      // user audio recorder
      if (includes(config.type, ["userAudioRecorder"])) {
        lastConfig.currentFile = pathOr(null, ["0"], answerAttachments);
        if (config.isVoiceToTextAfterRecording) {
          lastConfig.voiceToTextText = pathOr(
            null,
            ["0", "answer_description"],
            answer
          );
        }
      }

      return lastConfig;
    }
    return config;
  });
  extraDataObj = { ...extraDataObj, configs: updatedConfigs };

  // const typeId = pathOr(null, ["typeId"], extraDataObj);

  // let questionNumber = null;

  // if (!includes(typeId, [1, 2])) {
  //   questionNumber = currentPageStartQuestionNumber + index + 1;
  // }

  return {
    ...extraDataObj,
    code,
    itemNumber,
  };
};
export const getFormattedDateTime = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};
